import {
  Badge,
  BadgeVariantEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  Column,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  matchString,
  Table,
  TableOptions,
  WarningInfoIcon,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { groupItemsById } from '@/features/broadcast-schedule-program/helpers/group-by-id';
import { TSegmentOcr } from '@/features/segments/types';
import { getOffsetHHMMSS } from '@/utils/hh-mm-ss-into-number';

import { BroadcastScheduleProgramBodySegmentToolkitRotuloSubTable } from './BroadcastScheduleProgramBodySegmentToolkitRotuloSubTable';

type TBroadcastScheduleTableProps = {
  items: TSegmentOcr[];
  onPreview?: (item: TSegmentOcr) => void;
  onUpdate?: (item: TSegmentOcr) => void;
  onDelete?: (item: TSegmentOcr) => void;
};

export function BroadcastScheduleProgramBodySegmentToolkitRotuloTable({
  items,
  onPreview,
  onUpdate,
  onDelete,
}: TBroadcastScheduleTableProps) {
  const { activeSegment } = useBroadcastScheduleProgram();
  const { t } = useTranslation();

  const groupedOcr = useMemo(() => groupItemsById({ items, idKey: 'text' }), [items]);

  const options: Partial<TableOptions> = {
    initialSortBy: { id: 'updated_at', order: 'desc' },
    filters: [],
    searchBarPlaceholder: t('Buscar por ŕotulo'),
  };

  const columns: Column<TSegmentOcr>[] = useMemo(
    () => [
      {
        id: 'ocr',
        title: t('Rótulo'),
        searchable: true,
        render: item => {
          const label = item.text || item.id || '';
          return <TableRow content={label} className="!font-semibold" />;
        },
        matchFn: (query, item) => {
          return matchString(item?.text || '', query);
        },
      },
      {
        id: 'timeline',
        title: t('Timeline'),
        render: item => {
          if (!activeSegment) return <></>;

          const seconds = getOffsetHHMMSS(item.start_datetime, activeSegment.start_datetime);

          return item?.children && item.children?.length > 0 ? (
            <Badge variant={BadgeVariantEnum.DefaultFilled}>{item.children.length}</Badge>
          ) : (
            <TableRow content={seconds} />
          );
        },
      },
      {
        id: 'fiable',
        title: t('Revisado'),
        render: item => {
          return item?.children && item.children?.length > 0 ? (
            <>
              {item.children.some(child => child.review_state === 0) ? (
                <WarningInfoIcon className="text-feedback-error-20" />
              ) : null}
            </>
          ) : item.review_state === 1 ? (
            <TableRow className="!font-semibold !text-feedback-success-30" content={t('Si')} />
          ) : (
            <TableRow className="!font-semibold !text-feedback-warning-30" content={t('No')} />
          );
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: (item, _index, _rows, showSubArrows, subArrowsAreVisible) => (
          <div className="inline-flex w-full justify-end">
            {item?.children && item.children?.length > 0 ? (
              <>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton
                  color={IconButtonColorEnum.NONE_DEFAULT}
                  shape={IconButtonShapeEnumOld.OLD_SQUARED}
                  onClick={() => showSubArrows(prevVal => !prevVal)}
                >
                  {!subArrowsAreVisible ? (
                    <ChevronDownIcon size={IconSizeEnum.XS} />
                  ) : (
                    <ChevronUpIcon size={IconSizeEnum.XS} />
                  )}
                </IconButton>
              </>
            ) : (
              <RenderActionButtons
                item={item}
                onPreview={() => onPreview?.(item)}
                onUpdate={() => onUpdate?.(item)}
                onDelete={() => onDelete?.(item)}
              />
            )}
          </div>
        ),
      },
    ],
    [t, groupedOcr]
  );

  const MemoizedTable = useMemo(
    () => (
      <Table<any>
        options={options}
        columns={columns}
        data={groupedOcr}
        renderAdditionalRow={(item: TSegmentOcr) => (
          <BroadcastScheduleProgramBodySegmentToolkitRotuloSubTable
            item={item}
            columns={columns}
            onPreview={onPreview}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        )}
        ignoreWrap
      />
    ),
    [columns, items, activeSegment?.start_datetime]
  );

  return MemoizedTable;
}
